import type { Infer } from 'spacetimedb';
import {
  VisiblePlayersRow,
  VisiblePlayerIdentityRow,
  MyPlayerRow,
  CurrentMapRuntimeEntitiesRow,
  StatusEffectsRow,
  type ErrorContext,
  reducers
} from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type { SubscriptionFactory, TableLike } from '../core/SubscriptionFactory';
import type { QueuedIntent } from '../../model/gamestate/GameState';
import type {
  PlayerAppearanceUpdatePayload,
  PlayerSnapshot,
  PlayerPositionUpdatePayload,
  VisiblePlayerIdentitySnapshot,
  RuntimeEntitySnapshot,
  StatusEffectSnapshot,
  PlayerRosterSubscription,
  PlayerSubscriptionOptions,
  Logger
} from '../types';

type VisiblePlayersTableRow = Infer<typeof VisiblePlayersRow>;
type VisiblePlayerIdentityTableRow = Infer<typeof VisiblePlayerIdentityRow>;
type MyPlayerTableRow = Infer<typeof MyPlayerRow>;
type CurrentMapRuntimeEntitiesTableRow = Infer<typeof CurrentMapRuntimeEntitiesRow>;
type StatusEffectsTableRow = Infer<typeof StatusEffectsRow>;
const DEFAULT_RESOURCE_VALUE = 10_000;

export class PlayerModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly subscriptionFactory: SubscriptionFactory,
    private readonly logger: Logger = () => undefined
  ) { }

  public async fetchPlayerSnapshot(playerId: string): Promise<PlayerSnapshot | null> {
    const connection = await this.connectionManager.ensureConnection();

    const myPlayerMatch = Array.from(connection.db.myPlayer.iter()).find((row) => row.playerId === playerId);
    if (myPlayerMatch) {
      return this.mapMyPlayerRow(myPlayerMatch as unknown as MyPlayerTableRow);
    }

    const visibleMatch = Array.from(connection.db.visiblePlayers.iter()).find((row) => row.playerId === playerId);
    if (visibleMatch) {
      return this.mapVisiblePlayerRow(visibleMatch as unknown as VisiblePlayersTableRow);
    }

    return null;
  }

  public async waitForMyPlayerSnapshot(timeoutMs = 3000, pollIntervalMs = 50): Promise<PlayerSnapshot | null> {
    const connection = await this.connectionManager.ensureConnection();
    const deadline = Date.now() + timeoutMs;

    while (Date.now() <= deadline) {
      const myPlayerRows = Array.from(connection.db.myPlayer.iter());
      if (myPlayerRows.length > 0) {
        return this.mapMyPlayerRow(myPlayerRows[0] as unknown as MyPlayerTableRow);
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, pollIntervalMs);
      });
    }

    return null;
  }

  public async fetchPlayersOnMap(mapId: string): Promise<PlayerSnapshot[]> {
    if (!mapId) {
      return [];
    }

    const connection = await this.connectionManager.ensureConnection();
    const rows = connection.db.visiblePlayers.iter();
    return Array.from(rows)
      .filter((row) => row.currentMapId === mapId)
      .map((row) => this.mapVisiblePlayerRow(row as unknown as VisiblePlayersTableRow));
  }

  public async updatePlayerPosition(payload: PlayerPositionUpdatePayload): Promise<void> {
    const statusJson =
      typeof payload.statusJson === 'string'
        ? payload.statusJson
        : JSON.stringify(payload.statusJson ?? {});

    await this.reducerClient.callFireAndForget(
      'update_player_position',
      reducers.updatePlayerPosition.paramsType,
      {
        playerId: payload.playerId,
        currentInstanceId: payload.currentInstanceId,
        currentMapId: payload.currentMapId,
        mapX: payload.mapX,
        mapY: payload.mapY,
        rotation: payload.rotation,
        statusJson
      }
    );
  }

  public async setMyAppearance(payload: PlayerAppearanceUpdatePayload): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'set_my_appearance',
      reducers.setMyAppearance.paramsType,
      {
        colorHex: payload.colorHex,
        accessoriesJson: payload.accessoriesJson ?? '{}',
      }
    );
  }

  public async subscribeToAllPlayers(options: PlayerSubscriptionOptions & { currentPlayerId?: string }): Promise<PlayerRosterSubscription> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM visible_players',
        getTable: (db) => db.visiblePlayers,
        mapRow: (row) => this.mapVisiblePlayerRow(row as unknown as VisiblePlayersTableRow),
        sortFn: (a, b) => {
          const left = a.playerId ?? '';
          const right = b.playerId ?? '';
          return left.localeCompare(right);
        }
      },
      (rows) => {
        const payload = rows.filter((player) => Boolean(player.playerId));
        options.onUpdate(payload);
      }
    );
  }

  public async subscribeToMyPlayer(options: { onUpdate: (player: PlayerSnapshot | null) => void }): Promise<PlayerRosterSubscription> {
    return this.subscriptionFactory.createSingleRowSubscription(
      {
        sql: 'SELECT * FROM my_player',
        getTable: (db) => db.myPlayer,
        mapRow: (row) => this.mapMyPlayerRow(row as unknown as MyPlayerTableRow)
      },
      options.onUpdate
    );
  }

  public async subscribeToVisiblePlayerIdentity(
    options: { onUpdate: (players: VisiblePlayerIdentitySnapshot[]) => void }
  ): Promise<PlayerRosterSubscription> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM visible_player_identity',
        getTable: (db) => this.getVisiblePlayerIdentityTable(db as unknown as Record<string, unknown>),
        mapRow: (row) => this.mapVisiblePlayerIdentityRow(row as unknown as VisiblePlayerIdentityTableRow),
        sortFn: (a, b) => a.playerId.localeCompare(b.playerId),
      },
      options.onUpdate
    );
  }

  public async subscribeToMyStatusEffects(options: { onUpdate: (effects: StatusEffectSnapshot[]) => void }): Promise<PlayerRosterSubscription> {
    return this.subscriptionFactory.createArraySubscription(
      {
        sql: 'SELECT * FROM my_status_effects',
        getTable: (db) => (db as any).myStatusEffects || (db as any).my_status_effects,
        mapRow: (row) => this.mapStatusEffectRow(row as unknown as StatusEffectsTableRow),
        sortFn: (a, b) => a.effectId.localeCompare(b.effectId)
      },
      options.onUpdate
    );
  }

  public async subscribeToRuntimeEntitiesForInstance(
    options: { onUpdate: (entities: RuntimeEntitySnapshot[]) => void }
  ): Promise<PlayerRosterSubscription> {
    const connection = await this.connectionManager.ensureConnection();
    const table = this.getCurrentMapRuntimeEntitiesTable(
      connection.db as unknown as Record<string, unknown>
    );
    const runtimeEntities = new Map<string, RuntimeEntitySnapshot>();
    const orderedEntityIds: string[] = [];
    let emitScheduled = false;

    const insertEntityId = (entityId: string): void => {
      let low = 0;
      let high = orderedEntityIds.length;
      while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (orderedEntityIds[mid].localeCompare(entityId) < 0) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      if (orderedEntityIds[low] !== entityId) {
        orderedEntityIds.splice(low, 0, entityId);
      }
    };

    const flushRuntimeEntities = (): void => {
      emitScheduled = false;
      options.onUpdate(
        orderedEntityIds
          .map((entityId) => runtimeEntities.get(entityId))
          .filter((entity): entity is RuntimeEntitySnapshot => Boolean(entity))
      );
    };

    const emitRuntimeEntities = (): void => {
      if (emitScheduled) {
        return;
      }
      emitScheduled = true;
      queueMicrotask(flushRuntimeEntities);
    };

    const rebuildFromTable = (): void => {
      runtimeEntities.clear();
      orderedEntityIds.length = 0;
      for (const row of table.iter()) {
        const entity = this.mapRuntimeEntityRow(
          row as unknown as CurrentMapRuntimeEntitiesTableRow
        );
        runtimeEntities.set(entity.entityId, entity);
        insertEntityId(entity.entityId);
      }
      emitRuntimeEntities();
    };

    const handleInsert = (): void => {
      rebuildFromTable();
    };

    const handleDelete = (): void => {
      rebuildFromTable();
    };

    const handleUpdate = (): void => {
      rebuildFromTable();
    };

    table.onInsert(handleInsert);
    table.onDelete(handleDelete);
    table.onUpdate(handleUpdate);

    rebuildFromTable();

    const builder = connection.subscriptionBuilder();
    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) {
          return;
        }
        listenersRemoved = true;
        table.removeOnInsert(handleInsert);
        table.removeOnDelete(handleDelete);
        table.removeOnUpdate(handleUpdate);
      };

      builder
        .onApplied(() => {
          try {
            rebuildFromTable();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Runtime entities subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe('SELECT * FROM current_map_runtime_entities');
    });
  }

  private mapMyPlayerRow(row: MyPlayerTableRow): PlayerSnapshot {
    let status: unknown = null;
    const source = (row as any).statusJson ?? '{}';
    try {
      status = JSON.parse(source);
    } catch (error) {
      this.logger('player_status_parse_failed', {
        error: (error as Error).message,
        playerId: row.playerId
      });
      status = source;
    }

    const updatedAt = typeof row.updatedAt?.toDate === 'function' ? row.updatedAt.toDate() : new Date(0);

    let queue: QueuedIntent[] = [];
    const queueSource = (row as { queueJson?: string }).queueJson;
    if (queueSource) {
      try {
        queue = JSON.parse(queueSource) as QueuedIntent[];
      } catch (error) {
        this.logger('player_queue_parse_failed', {
          error: (error as Error).message,
          playerId: row.playerId
        });
      }
    }

    return {
      playerId: row.playerId,
      accountId: row.accountId,
      displayName: row.displayName,
      color: '#3b82f6',
      level: row.level,
      xp: Number(row.xp ?? 0),
      currentInstanceId: row.currentInstanceId,
      currentMapId: row.currentMapId,
      channelId: row.channelId,
      mapX: row.mapX,
      mapY: row.mapY,
      prevMapX: row.prevMapX,
      prevMapY: row.prevMapY,
      rotation: row.rotation,
      hp: (row as any).hp ?? DEFAULT_RESOURCE_VALUE,
      maxHp: (row as any).maxHp ?? DEFAULT_RESOURCE_VALUE,
      mana: (row as any).mana ?? DEFAULT_RESOURCE_VALUE,
      maxMana: (row as any).maxMana ?? DEFAULT_RESOURCE_VALUE,
      stamina: (row as any).stamina ?? DEFAULT_RESOURCE_VALUE,
      maxStamina: (row as any).maxStamina ?? DEFAULT_RESOURCE_VALUE,
      status,
      updatedAt,
      queue,
      inventoryCapacity: row.inventoryCapacity,
      storageCapacity: Number((row as { storageCapacity?: number }).storageCapacity ?? 100),
      moveDistance: Number((row as { moveDistance?: number }).moveDistance ?? 1),
      viewRadius: Number((row as { viewRadius?: number }).viewRadius ?? 5),
      isOnline: row.isOnline,
      isEntering: (row as any).isEntering,
      isExiting: (row as any).isExiting
    };
  }

  private mapVisiblePlayerRow(row: VisiblePlayersTableRow): PlayerSnapshot {
    let status: unknown = null;
    const source = row.statusJson ?? '{}';
    try {
      status = JSON.parse(source);
    } catch (error) {
      this.logger('player_status_parse_failed', {
        error: (error as Error).message,
        playerId: row.playerId
      });
      status = source;
    }

    const updatedAt = typeof row.updatedAt?.toDate === 'function' ? row.updatedAt.toDate() : new Date(0);

    return {
      playerId: row.playerId,
      accountId: '',
      displayName: row.displayName,
      color: '#f59e0b',
      level: row.level,
      xp: 0,
      currentInstanceId: row.currentInstanceId,
      currentMapId: row.currentMapId,
      channelId: '',
      mapX: row.mapX,
      mapY: row.mapY,
      prevMapX: row.prevMapX,
      prevMapY: row.prevMapY,
      rotation: row.rotation,
      hp: (row as any).hp ?? DEFAULT_RESOURCE_VALUE,
      maxHp: (row as any).maxHp ?? DEFAULT_RESOURCE_VALUE,
      mana: (row as any).mana ?? DEFAULT_RESOURCE_VALUE,
      maxMana: (row as any).maxMana ?? DEFAULT_RESOURCE_VALUE,
      stamina: (row as any).stamina ?? DEFAULT_RESOURCE_VALUE,
      maxStamina: (row as any).maxStamina ?? DEFAULT_RESOURCE_VALUE,
      status,
      updatedAt,
      queue: [],
      inventoryCapacity: 0,
      storageCapacity: 100,
      moveDistance: 1,
      viewRadius: 5,
      isOnline: row.isOnline,
      isEntering: (row as any).isEntering,
      isExiting: (row as any).isExiting
    };
  }

  private mapVisiblePlayerIdentityRow(
    row: VisiblePlayerIdentityTableRow
  ): VisiblePlayerIdentitySnapshot {
    const rowAny = row as unknown as { guildName?: string | null };
    return {
      playerId: row.playerId,
      displayName: row.displayName,
      guildName:
        typeof rowAny.guildName === 'string' && rowAny.guildName.length > 0
          ? rowAny.guildName
          : undefined,
    };
  }

  private mapRuntimeEntityRow(row: CurrentMapRuntimeEntitiesTableRow): RuntimeEntitySnapshot {
    const rowAny = row as any;
    const fallbackPosition = {
      x: typeof rowAny.mapX === 'number' ? rowAny.mapX : 0,
      y: typeof rowAny.mapY === 'number' ? rowAny.mapY : 0
    };
    let position = fallbackPosition;

    const rawPositionTile = (row as any).positionTile;
    if (typeof rawPositionTile === 'string' && rawPositionTile.length > 0) {
      try {
        const parsed = JSON.parse(rawPositionTile) as { x?: unknown; y?: unknown };
        const x = typeof parsed.x === 'number' ? parsed.x : fallbackPosition.x;
        const y = typeof parsed.y === 'number' ? parsed.y : fallbackPosition.y;
        position = { x, y };
      } catch (error) {
        this.logger('runtime_entity_position_parse_failed', {
          error: (error as Error).message,
          entityId: row.entityId
        });
      }
    }

    return {
      entityId: row.entityId,
      entityClass: rowAny.entityClass,
      entityType: row.entityType,
      appearanceColorHex:
        typeof rowAny.appearanceColorHex === 'string' ? rowAny.appearanceColorHex : undefined,
      appearanceAccessoriesJson:
        typeof rowAny.appearanceAccessoriesJson === 'string'
          ? rowAny.appearanceAccessoriesJson
          : undefined,
      playerId:
        typeof rowAny.playerId === 'string'
          ? rowAny.playerId
          : rowAny.entityClass === 'player' || row.entityType === 'player'
            ? row.entityId
            : null,
      mapX: typeof rowAny.mapX === 'number' ? rowAny.mapX : position.x,
      mapY: typeof rowAny.mapY === 'number' ? rowAny.mapY : position.y,
      prevMapX: typeof rowAny.prevMapX === 'number' ? rowAny.prevMapX : undefined,
      prevMapY: typeof rowAny.prevMapY === 'number' ? rowAny.prevMapY : undefined,
      rotation: typeof rowAny.rotation === 'number' ? rowAny.rotation : 180,
      hp: typeof rowAny.hp === 'number' ? rowAny.hp : DEFAULT_RESOURCE_VALUE,
      maxHp: typeof rowAny.maxHp === 'number' ? rowAny.maxHp : DEFAULT_RESOURCE_VALUE,
      mana: typeof rowAny.mana === 'number' ? rowAny.mana : undefined,
      maxMana: typeof rowAny.maxMana === 'number' ? rowAny.maxMana : undefined,
      stamina: typeof rowAny.stamina === 'number' ? rowAny.stamina : DEFAULT_RESOURCE_VALUE,
      maxStamina: typeof rowAny.maxStamina === 'number' ? rowAny.maxStamina : DEFAULT_RESOURCE_VALUE,
      mobInventoryValue: typeof rowAny.mobInventoryValue === 'number' ? rowAny.mobInventoryValue : null,
      mobInventoryItemsJson:
        typeof rowAny.mobInventoryItemsJson === 'string' ? rowAny.mobInventoryItemsJson : null,
      isEntering: rowAny.isEntering,
      isExiting: rowAny.isExiting,
      position
    };
  }

  private mapStatusEffectRow(row: StatusEffectsTableRow): StatusEffectSnapshot {
    const rowAny = row as any;
    const expiresRaw = rowAny.expiresAtTick ?? rowAny.expires_at_tick ?? null;
    return {
      effectId: row.effectId,
      effectType: row.effectType,
      sourceId: row.sourceId,
      targetId: row.targetId,
      skillId: row.skillId,
      damage: row.damage,
      expiresAtTick: expiresRaw == null ? null : Number(expiresRaw)
    };
  }

  private getCurrentMapRuntimeEntitiesTable(db: Record<string, unknown>): TableLike<CurrentMapRuntimeEntitiesTableRow> {
    const table = (db.currentMapRuntimeEntities ?? db.current_map_runtime_entities) as TableLike<CurrentMapRuntimeEntitiesTableRow> | undefined;
    if (!table) {
      throw new Error('current_map_runtime_entities view accessor is unavailable in generated bindings');
    }
    return table;
  }

  private getVisiblePlayerIdentityTable(
    db: Record<string, unknown>
  ): TableLike<VisiblePlayerIdentityTableRow> {
    const table = (db.visiblePlayerIdentity ?? db.visible_player_identity) as
      | TableLike<VisiblePlayerIdentityTableRow>
      | undefined;
    if (!table) {
      throw new Error('visible_player_identity view accessor is unavailable in generated bindings');
    }
    return table;
  }
}
